import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Calendar, 
  Clock, 
  ChevronRight, 
  Search,
  Tag,
  Eye,
  Loader2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useArticles } from '../hooks/useArticles';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentWrapper = styled.div`
  margin-top: -2rem;
  position: relative;
  z-index: 2;
`;

const SearchFilterBar = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-md);
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  border: 1px solid var(--border-light);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchBox = styled.div`
  flex: 1;
  position: relative;
  min-width: 250px;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-light);
  }

  input {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 2.75rem;
    border: 2px solid var(--border-light);
    border-radius: var(--radius-md);
    font-size: 0.95rem;
    transition: var(--transition);

    &:focus {
      outline: none;
      border-color: var(--primary-blue);
    }
  }
`;

const FilterSelect = styled.select`
  padding: 0.875rem 2.5rem 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  background: var(--white);
  cursor: pointer;
  min-width: 200px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const FeaturedArticle = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  margin-bottom: 2rem;
  border: 1px solid var(--border-light);
  display: grid;
  grid-template-columns: 1.2fr 1fr;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedImage = styled.div`
  height: 100%;
  min-height: 350px;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.3);
  font-size: 4rem;
`;

const FeaturedContent = styled.div`
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--accent-gold);
  color: var(--primary-blue-dark);
  padding: 0.4rem 1rem;
  border-radius: var(--radius-full);
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  width: fit-content;
  margin-bottom: 1rem;
`;

const FeaturedTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.75rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  font-weight: 700;
  line-height: 1.3;
`;

const FeaturedExcerpt = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: var(--text-light);

  svg {
    color: var(--primary-blue);
    opacity: 0.6;
  }
`;

const ReadMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-blue);
  font-weight: 700;
  font-size: 1rem;
  transition: var(--transition);

  &:hover {
    color: var(--secondary-red);

    svg {
      transform: translateX(4px);
    }
  }

  svg {
    transition: var(--transition);
  }
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ArticleCard = styled(Link)`
  background: var(--white);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  transition: var(--transition);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: transparent;

    .card-image {
      transform: scale(1.05);
    }
  }
`;

const CardImageWrapper = styled.div`
  height: 200px;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  overflow: hidden;
  position: relative;

  .card-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255,255,255,0.3);
  }
`;

const CardCategory = styled.span`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: var(--accent-gold);
  color: var(--primary-blue-dark);
  padding: 0.35rem 0.875rem;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  z-index: 2;
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-family: var(--font-secondary);
  font-size: 1.15rem;
  color: var(--text-dark);
  margin-bottom: 0.75rem;
  font-weight: 700;
  line-height: 1.4;
  transition: var(--transition);

  ${ArticleCard}:hover & {
    color: var(--primary-blue);
  }
`;

const CardResume = styled.p`
  font-size: 0.95rem;
  color: var(--text-medium);
  line-height: 1.6;
  margin-bottom: 1rem;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid var(--border-light);
  font-size: 0.85rem;
  color: var(--text-light);
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 3rem;
`;

const PageButton = styled.button`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: var(--transition);
  background: ${props => props.$active ? 'var(--primary-blue)' : 'var(--white)'};
  color: ${props => props.$active ? 'var(--white)' : 'var(--text-dark)'};
  border: 1px solid ${props => props.$active ? 'var(--primary-blue)' : 'var(--border-light)'};

  &:hover {
    border-color: var(--primary-blue);
    color: ${props => props.$active ? 'var(--white)' : 'var(--primary-blue)'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);

  svg {
    color: var(--border-light);
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.25rem;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-light);
  }
`;



const Actualites = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const { articles, loading, error } = useArticles();
  const hasActiveFilters = searchTerm.trim() !== '' || filter !== 'all';

  // Prendre le premier article comme featured, les autres comme réguliers
  const featuredArticle = articles.length > 0 ? articles[0] : null;
  const regularArticles = articles.length > 1 ? articles.slice(1) : [];
  const articlesForGrid = hasActiveFilters ? articles : regularArticles;

  const filteredArticles = articlesForGrid.filter(article => {
    const searchableText = `${article.title || ''} ${article.resume || article.excerpt || ''}`.toLowerCase();
    const matchesSearch = searchableText.includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || article.category === filter;
    return matchesSearch && matchesFilter;
  });

  const categories = ['all', ...new Set(articles.map(a => a.category))];

  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Actualités</HeroTitle>
          <HeroSubtitle>
            Restez informés des dernières nouvelles et activités de la Médiature de la République
          </HeroSubtitle>
        </HeroContent>
      </Hero>

      <Container>
        <ContentWrapper>
          {/* Barre de recherche et filtres */}
          <SearchFilterBar>
            <SearchBox>
              <Search size={20} />
              <input
                type="text"
                placeholder="Rechercher une actualité..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
            <FilterSelect 
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">Toutes les catégories</option>
              {categories.filter(c => c !== 'all').map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </FilterSelect>
          </SearchFilterBar>

          {/* Loading state */}
          {loading && (
            <EmptyState>
              <Loader2 size={48} className="animate-spin" />
              <h3>Chargement...</h3>
            </EmptyState>
          )}

          {/* Error state */}
          {error && (
            <EmptyState>
              <h3>Aucun article disponible</h3>
              <p>Les articles seront bientôt publiés.</p>
            </EmptyState>
          )}

          {/* Article à la une */}
          {!loading && featuredArticle && !hasActiveFilters && (
            <FeaturedArticle>
              <FeaturedImage>
                {featuredArticle.featuredImage ? (
                  <img 
                    src={featuredArticle.featuredImage} 
                    alt={featuredArticle.title} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                ) : (
                  <span style={{ fontSize: '5rem' }}>📰</span>
                )}
              </FeaturedImage>
              <FeaturedContent>
                <CategoryBadge>
                  <Tag size={14} />
                  {featuredArticle.category}
                </CategoryBadge>
                <FeaturedTitle>{featuredArticle.title}</FeaturedTitle>
                <FeaturedExcerpt>{featuredArticle.resume}</FeaturedExcerpt>
                <MetaInfo>
                  <MetaItem>
                    <Calendar size={16} />
                    {featuredArticle.createdAt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </MetaItem>
                  <MetaItem>
                    <Clock size={16} />
                    {featuredArticle.readTime || '3 min'}
                  </MetaItem>
                  <MetaItem>
                    <Eye size={16} />
                    {featuredArticle.views || 0} vues
                  </MetaItem>
                </MetaInfo>
                <ReadMoreLink to={`/actualites/${featuredArticle.id}`}>
                  Lire l'article <ChevronRight size={20} />
                </ReadMoreLink>
              </FeaturedContent>
            </FeaturedArticle>
          )}

          {/* Grille d'articles */}
          {!loading && filteredArticles.length > 0 ? (
            <ArticlesGrid>
              {filteredArticles.map(article => (
                <ArticleCard key={article.id} to={`/actualites/${article.id}`}>
                  <CardImageWrapper>
                    <div className="card-image">
                      {article.featuredImage ? (
                        <img 
                          src={article.featuredImage} 
                          alt={article.title} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        />
                      ) : (
                        <span style={{ fontSize: '3rem' }}>📰</span>
                      )}
                    </div>
                    <CardCategory>{article.category}</CardCategory>
                  </CardImageWrapper>
                  <CardContent>
                    <CardTitle>{article.title}</CardTitle>
                    <CardResume>{article.resume}</CardResume>
                    <CardMeta>
                      <MetaItem>
                        <Calendar size={14} />
                        {article.createdAt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </MetaItem>
                      <MetaItem>
                        <Clock size={14} />
                        {article.readTime || '3 min'}
                      </MetaItem>
                    </CardMeta>
                  </CardContent>
                </ArticleCard>
              ))}
            </ArticlesGrid>
          ) : !loading && (!featuredArticle || hasActiveFilters) && (
            <EmptyState>
              <Search size={64} />
              <h3>Aucun article trouvé</h3>
              <p>{hasActiveFilters ? 'Aucun résultat pour votre recherche.' : 'Les articles seront bientôt disponibles'}</p>
            </EmptyState>
          )}

          {/* Pagination */}
          {!loading && filteredArticles.length > 0 && (
            <Pagination>
              <PageButton>←</PageButton>
              <PageButton $active>1</PageButton>
              <PageButton>→</PageButton>
            </Pagination>
          )}
        </ContentWrapper>
      </Container>
    </PageContainer>
  );
};

export default Actualites;
